import { computed, type MaybeRefOrGetter, toValue } from 'vue';
import { usePermissions } from '@/composables';
import { Permission, PermissionAction } from '@/constants/permissions';
import { CONTEXT_AVAILABLE_COLUMNS } from '../TaskListColumnToggle/constants';
import { TABLE_COLUMN } from '../constants';

export interface UseTaskListAvailableColumnsOptions {
	useTaskStatuses?: MaybeRefOrGetter<boolean>;
	useSubtasks?: MaybeRefOrGetter<boolean>;
}

/**
 * Returns available columns filtered by user permissions and settings
 */
export const useTaskListAvailableColumns = (
	options: UseTaskListAvailableColumnsOptions = {}
) => {
	const { can } = usePermissions();

	const canSeeBudgetColumns = computed(() =>
		can(
			PermissionAction.HAS_PERMISSION,
			Permission.CAN_SEE_QUOTE_BUDGETED_HOURS_ON_TASKS
		)
	);

	const filterBudgetColumns = (columns: readonly string[]): string[] => {
		if (canSeeBudgetColumns.value) {
			return [...columns];
		}

		return columns.filter(
			(col) =>
				col !== TABLE_COLUMN.Budget &&
				col !== TABLE_COLUMN.RemainingBudget
		);
	};

	const filterBySettings = (columns: string[]): string[] => {
		let filtered = [...columns];

		if (
			options.useTaskStatuses !== undefined &&
			!toValue(options.useTaskStatuses)
		) {
			filtered = filtered.filter((col) => col !== TABLE_COLUMN.Status);
		}

		if (
			options.useSubtasks !== undefined &&
			!toValue(options.useSubtasks)
		) {
			filtered = filtered.filter((col) => col !== TABLE_COLUMN.Subitems);
		}

		return filtered;
	};

	const taskListAvailableColumns = computed(() => {
		const filtered = filterBudgetColumns(
			CONTEXT_AVAILABLE_COLUMNS.TASK_LIST
		);

		return filterBySettings(filtered);
	});

	const projectTaskListAvailableColumns = computed(() => {
		const filtered = filterBudgetColumns(
			CONTEXT_AVAILABLE_COLUMNS.PROJECT_TASK_LIST
		);

		return filterBySettings(filtered);
	});

	const subtaskListAvailableColumns = computed(() => {
		const filtered = [...CONTEXT_AVAILABLE_COLUMNS.SUBTASK_LIST];

		return filterBySettings(filtered);
	});

	return {
		canSeeBudgetColumns,
		taskListAvailableColumns,
		projectTaskListAvailableColumns,
		subtaskListAvailableColumns,
	};
};
